import {
  DeleteTwoTone,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import { Button, Input, Space, Table, Tag } from "antd";
import React, { useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { useNavigate } from "react-router-dom";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import { getErrorMessage } from "../../service/api";
import saleService from "../../service/saleService";
import { formatDateSafe } from "../../utils/formatters";
import ResponsiveDialog from "../dialog/dialog";
import SimpleBackdrop from "../loading/loading";

const NewTableComponent = ({ data, onPageChange, onFilterChange, onSortChange }) => {
  const {
    getPermission,
    checkPermissionWithRefresh,
    permissionError,
    clearPermissionError,
  } = usePermissionCheck();

  // Obter permissões específicas
  const permissionSale = getPermission("Vendas");
  const permSendSale = getPermission("Enviar Vendas");
  const permissionReopen = getPermission("Reabrir Vendas");

  let navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const [activeFilters, setActiveFilters] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Estados inicializados com valores padrão para evitar valores undefined
  const [openDialog, setOpenDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState("");
  const [titleDialog, setTitleDialog] = useState("");
  const [statusDialog, setStatusDialog] = useState("");

  const [openDialogFree, setOpenDialogFree] = useState(false);
  const [messageDialogFree, setMessageDialogFree] = useState("");
  const [titleDialogFree, setTitleDialogFree] = useState("");
  const [statusDialogFree, setStatusDialogFree] = useState("");

  // Evita duplo clique em ações assíncronas (enviar/reabrir)
  const [actionRunning, setActionRunning] = useState(false);

  const [openLoading, setOpenLoading] = useState(false);

  // Funções para verificar permissões antes de executar ações
  const handleEditSale = async (record) => {
    try {
      const hasPermission = await checkPermissionWithRefresh("Vendas", "isinsert", true, false);
      const id = getSaleId(record);
      if (hasPermission) {
        if (id) {
          navigate(`/sale/${id}`);
        } else {
          setOpenDialog(true);
          setStatusDialog("error");
          setTitleDialog("Ops!");
          setMessageDialog("ID da venda inválido. Não é possível editar.");
        }
      }
    } catch (err) {
      // Se algo der errado na checagem de permissão, exibir erro amigável
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(err) || "Erro ao verificar permissão.");
    }
  };

  const handleDeleteSale = async (record) => {
    try {
      const hasPermission = await checkPermissionWithRefresh("Vendas", "isremove", true, false);
      const id = getSaleId(record);
      if (hasPermission) {
        if (id) {
          deleteService(id);
        } else {
          setOpenDialog(true);
          setStatusDialog("error");
          setTitleDialog("Ops!");
          setMessageDialog("ID da venda inválido. Não é possível deletar.");
        }
      }
    } catch (err) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(err) || "Erro ao verificar permissão.");
    }
  };

  const handleSendSale = async (record) => {
    try {
      if (actionRunning) return;
      setActionRunning(true);
      const hasPermission = await checkPermissionWithRefresh("Enviar Vendas", "isinsert", true, false);
      const id = getSaleId(record);
      if (hasPermission) {
        if (id) {
          sendSale(id);
        } else {
          setOpenDialog(true);
          setStatusDialog("error");
          setTitleDialog("Ops!");
          setMessageDialog("ID da venda inválido. Não é possível enviar.");
        }
      }
    } catch (err) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(err) || "Erro ao verificar permissão.");
    } finally {
      setActionRunning(false);
    }
  };

  function refreshPage() {
    window.location.reload(false);
  }

  const handleReopenSale = async (record) => {
    try {
      if (actionRunning) return;
      setActionRunning(true);
      const hasPermission = await checkPermissionWithRefresh("Reabrir Vendas", "isinsert", true, false);
      const id = getSaleId(record);
      if (hasPermission) {
        if (id) {
          try {
            navigate(`/saleReopen/${id}/true`);
          } catch (navErr) {
            setOpenDialog(true);
            setStatusDialog("error");
            setTitleDialog("Ops!");
            setMessageDialog(getErrorMessage(navErr) || "Erro ao reabrir venda");
          }
        } else {
          setOpenDialog(true);
          setStatusDialog("error");
          setTitleDialog("Ops!");
          setMessageDialog("ID da venda inválido. Não é possível reabrir.");
        }
      } else {
        setOpenDialog(true);
        setStatusDialog("error");
        setTitleDialog("Ops!");
        setMessageDialog("Você não tem permissão para reabrir esta venda");
      }
    } catch (err) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(err) || "Erro ao verificar permissão.");
    } finally {
      setActionRunning(false);
    }
  };

  const handleViewSale = async (record) => {
    const id = getSaleId(record);
    if (id) {
      navigate(`/sale/${id}`);
    } else {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog("ID da venda inválido. Não é possível visualizar.");
    }
  };

  // Utilitário para obter o id da venda de forma segura (evita navegar para /sale/undefined)
  const getSaleId = (record) => {
    if (!record) return null;
    return record.idsale || record.idSale || record.id || null;
  };

  /**
   * @function mapColumnToBackend
   * @description Mapear nomes das colunas do frontend para o backend
   * @param {string} columnName - Nome da coluna do frontend
   * @returns 
   */
  const mapColumnToBackend = (columnName) => {
    const mapping = {
      'client': 'customer',
      'name': 'name',
      'situation': 'situation',
      'codpi': 'codPi',
      'codintern': 'codIntern',
      'datesale': 'dateSale',
      'vlafaturar': 'vlTotal',
      'ticketmiddle': 'ticketMiddle',
      'qtdpanel': 'qtdPanel',
    };
    
    const normalizedColumn = columnName.toLowerCase();
    return mapping[normalizedColumn] || columnName;
  };

  /**
   * @function handleTableChange
   * @description Manipula mudanças na tabela (paginação, ordenação, filtros)
   */
  const handleTableChange = (pagination, filters, sorter) => {
    console.log("🔄 [TableSale] Mudança na tabela:", { pagination, filters, sorter });
    
    // Tratar ordenação
    if (sorter && sorter.field) {
      const field = mapColumnToBackend(sorter.field);
      const order = sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : null;
      
      console.log("📊 [TableSale] Ordenação:", { field, order });
      
      setSortField(field);
      setSortOrder(order);
      
      if (onSortChange && order) {
        onSortChange(field, order);
      } else if (onSortChange && !order) {
        // Se removeu a ordenação, notifica com null
        onSortChange(null, null);
      }
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex, close) => {
    console.log("🔍 [TableSale] Aplicando filtro:", dataIndex, "=", selectedKeys[0]);
    
    if (selectedKeys[0] && selectedKeys[0].trim() !== "") {
      // Criar novo objeto de filtros ANTES de atualizar o estado
      const newFilters = {
        ...activeFilters,
        [dataIndex]: selectedKeys[0]
      };
      
      // Mapear colunas para formato do backend
      const backendFilters = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        const backendKey = mapColumnToBackend(key);
        backendFilters[backendKey] = value;
      });
      
      console.log("📡 [TableSale] Filtros frontend:", newFilters);
      console.log("📡 [TableSale] Filtros backend:", backendFilters);
      
      // Notificar componente pai IMEDIATAMENTE (antes de atualizar estados)
      if (onFilterChange) {
        onFilterChange(backendFilters);
      }
      
      // DEPOIS atualizar estados locais (apenas para UI)
      setActiveFilters(newFilters);
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
    }
    
    // Fechar dropdown sem confirmar filtro (porque não tem onFilter)
    if (close) {
      close();
    }
  };

  const handleReset = (clearFilters, dataIndex) => {
    console.log("🧹 [TableSale] Limpando filtro:", dataIndex);
    
    if (clearFilters) {
      clearFilters();
    }
    
    if (searchedColumn === dataIndex) {
      setSearchText("");
      setSearchedColumn("");
    }

    const newFilters = { ...activeFilters };
    delete newFilters[dataIndex];
    setActiveFilters(newFilters);

    const backendFilters = {};
    Object.entries(newFilters).forEach(([key, value]) => {
      const backendKey = mapColumnToBackend(key);
      backendFilters[backendKey] = value;
    });

    // Notificar componente pai sobre mudança nos filtros
    if (onFilterChange) {
      console.log("📡 [TableSale] Filtros frontend após reset:", newFilters);
      console.log("📡 [TableSale] Filtros backend após reset:", backendFilters);
      onFilterChange(backendFilters);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
          position: "fixed",
          background: "#FFF",
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Pesquisar ${dataIndex}`}
          value={selectedKeys?.[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex, close)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />

        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex, close)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Procurar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Limpar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Fechar
          </Button>
        </Space>
      </div>
    ),

    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: activeFilters[dataIndex] ? "#1677ff" : undefined,
        }}
      />
    ),

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const showStatusSituationSale = (status) => {
    switch (status) {
      case "Em Orcamento":
        return "geekblue";
      case "Venda":
        return "success";
      case "Em Autorizacao":
        return "warning";
      case "Orcamento recusado":
        return "error";
      default:
        return "default";
    }
  };

  const showLog = (log) => {
    setOpenDialogFree(true);
    setStatusDialogFree("warning");
    if (!log) {
      setTitleDialogFree("Log");
      setMessageDialogFree("");
      return;
    }
    if (typeof log === "string") {
      setTitleDialogFree("Log");
      setMessageDialogFree(log);
      return;
    }
    setTitleDialogFree(log.title || "Log");
    setMessageDialogFree(log.description || String(log));
  };

  const deleteService = async (id) => {
    try {
      const response = await saleService.delete(id);
      if (response.status === 200 || response.status === 201 || response.success === true) {
        setOpenDialog(true);
        setStatusDialog("success");
        setTitleDialog("Muito bem!");
        setMessageDialog("Venda deletada com sucesso");
        setTimeout(() => {
          refreshPage();
        }, 1000);
      } else {
        setOpenDialog(true);
        setStatusDialog("error");
        setTitleDialog("Ops!");
        setMessageDialog(getErrorMessage(response) || "Erro ao deletar venda");
      }
    } catch (error) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(error) || "Erro ao deletar venda");
    }
  };

  const sendSale = async (id) => {
    try {
      setOpenLoading(true);
      var saleData = {
        issend: true,
        idsale: id,
      };

      const response = await saleService.sendSale(saleData);
      if (response.status === 200 || response.status === 201 || response.success === true) {
        setOpenDialog(true);
        setStatusDialog("success");
        setTitleDialog("Muito bem!");
        setMessageDialog(
          response.message ||
          "Venda enviada com sucesso, nesse momento a venda não pode ser mais alterada."
        );
      } else {
        setOpenDialog(true);
        setStatusDialog("error");
        setTitleDialog("Ops!");
        setMessageDialog(getErrorMessage(response) || "Erro ao enviar venda");
      }
    } catch (error) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(getErrorMessage(error) || "Erro ao enviar venda");
    } finally {
      setOpenLoading(false);
    }
  };

  const columns = [
    {
      title: "Data Venda",
      dataIndex: "dateSale",
      key: "dateSale",
      width: "13%",
      ...getColumnSearchProps("dateSale"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
      render: (dateSale) => formatDateSafe(dateSale),
    },
    {
      title: "Código P.I",
      dataIndex: "codPi",
      width: "13%",
      key: "codPi",
      ...getColumnSearchProps("codPi"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Código Interno",
      dataIndex: "codIntern",
      width: "13%",
      key: "codIntern",
      ...getColumnSearchProps("codIntern"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Cliente",
      dataIndex: "client",
      key: "client",
      width: "55%",
      ...getColumnSearchProps("client"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
    },

    {
      title: "Campanha",
      dataIndex: "name",
      key: "name",
      width: "55%",
      ...getColumnSearchProps("name"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Status",
      dataIndex: "situation",
      key: "situation",
      ...getColumnSearchProps("situation"),
      sorter: true, // Usa ordenação do backend
      sortDirections: ["ascend", "descend"],
      render: (tags) => (
        <span>
          <Tag color={showStatusSituationSale(tags)} key={1}>
            {tags}
          </Tag>
        </span>
      ),
    },
    {
      title: "Valor",
      dataIndex: "vlAFaturar",
      key: "vlAFaturar",
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
      render: (_, record) => {
        const val = Number(record?.vlAFaturar);
        const totalContrato = Number(record?.totalContrato);
        if (!isFinite(val)) return <span>-</span>;
        return (
          <span>
            <>
              <Tag>
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(val)}
              </Tag>
              <span style={{ fontSize: "12px" }}>
                {isFinite(totalContrato) && totalContrato > 0
                  ? new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(totalContrato)
                  : ""}
              </span>
            </>
          </span>
        );
      },
    },

    {
      title: "Ticket Médio",
      dataIndex: "ticketMiddle",
      key: "ticketMiddle",
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
      render: (ticket) => {
        const t = Number(ticket);
        if (!isFinite(t)) return <span>-</span>;
        return <span>{new Intl.NumberFormat("pt-BR").format(t)}</span>;
      },
    },
    {
      title: "Painéis",
      dataIndex: "qtdPanel",
      key: "qtdPanel",
      sorter: true, // Usa ordenação do backend
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {permissionSale?.isinsert && !record?.issend ? (
            <EditOutlined
              style={{
                fontSize: "20px",
                color: "#4169E1",
              }}
                onClick={(e) => {
                  if (e && typeof e.preventDefault === "function") {
                    e.preventDefault();
                  }
                  handleEditSale(record);
                }}
            />
          ) : (
            <EyeOutlined
              title="Visualizar venda"
              style={{
                fontSize: "20px",
                color: "#4169E1",
              }}
              onClick={(e) => {
                if (e && typeof e.preventDefault === "function") {
                  e.preventDefault();
                }
                handleViewSale(record);
              }}
            />
          )}

          {permissionSale?.isremove && !record.issend ? (
            <DeleteTwoTone
              twoToneColor="#DC143C"
              style={{
                fontSize: "20px",
                cursor: "pointer",
              }}
              onClick={() => handleDeleteSale(record)}
            />
          ) : null}

          {permSendSale?.isinsert && !record.issend ? (
            <SendOutlined
              style={{
                fontSize: "20px",
                color: "#008000",
                cursor: "pointer",
              }}
              onClick={() => handleSendSale(record)}
            />
          ) : null}

          {record.logError?.title && !record.issend ? (
            <WarningOutlinedIcon
              onClick={(e) => showLog(record.logError)}
              color="warning"
              style={{ cursor: "pointer" }}
            />
          ) : null}

          {permissionReopen?.isinsert === true && record?.issend === true ? (
            <LockOutlined
              title="Reabrir venda"
              alt="Reabrir venda"
              style={{
                fontSize: "18px",
                color: "#4169E1",
                cursor: "pointer",
              }}
              onClick={() => handleReopenSale(record)}
            />
          ) : null}
        </Space>
      ),
    },
  ];

  const handleDialogCloseFree = () => {
    setOpenDialogFree(false);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    navigate("/listSale");
    setTimeout(() => {
      refreshPage();
    }, 1000);
  };

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  return (
    <>
      <SimpleBackdrop 
        open={openLoading} 
        handleClose={() => setOpenLoading(false)} 
        title={"Enviando venda..."} 
      />

      <ResponsiveDialog
        open={openDialog}
        handleClose={handleDialogClose}
        title={titleDialog}
        message={messageDialog}
        status={statusDialog}
      />

      <ResponsiveDialog
        open={openDialogFree}
        handleClose={handleDialogCloseFree}
        title={titleDialogFree}
        message={messageDialogFree}
        status={statusDialogFree}
      />

      {/* Dialog de erro de permissão */}
      <ResponsiveDialog
        open={!!permissionError}
        handleClose={clearPermissionError}
        title={permissionError?.title || "Erro de Permissão"}
        message={permissionError?.message || "Você não tem permissão para acessar este recurso."}
        status={permissionError?.type || "warning"}
      />

      <Table 
        columns={columns} 
        dataSource={Array.isArray(data?.data) ? data.data : []} 
        rowKey={(record) => getSaleId(record) || JSON.stringify(record)}
        onChange={handleTableChange}
        pagination={{
          current: data?.pagination?.currentPage || 1,
          pageSize: data?.pagination?.itemsPerPage || 10,
          total: data?.pagination?.totalItems || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            console.log("🔄 [TableSale] Mudando para página:", page, "Tamanho:", pageSize);
            if (onPageChange) {
              onPageChange(page, pageSize);
            }
          },
          onShowSizeChange: (current, size) => {
            console.log("🔄 [TableSale] Mudando tamanho da página:", size);
            if (onPageChange) {
              onPageChange(1, size);
            }
          }
        }}
      />
    </>
  );
};

export default NewTableComponent;
